import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { joinGroupByInvite } from '../services/groupService';

const GroupInviteHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);
    
    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  useEffect(() => {
    const processInvite = async () => {
      try {
        // Check if user is online
        if (!navigator.onLine) {
          setStatus('error');
          setErrorMessage('Du musst online sein, um einer Gruppe beizutreten. Bitte stelle eine Internetverbindung her und versuche es erneut.');
          return;
        }

        // Extract invite code from URL hash
        const hash = location.hash;
        
        if (!hash || hash.length <= 1) {
          setStatus('error');
          setErrorMessage('Kein Einladungscode in der URL gefunden.');
          return;
        }

        // Remove the leading # from hash
        const inviteCode = hash.substring(1);
        
        console.log('Processing group invite with code:', inviteCode);
        
        // Join the group using the invite code
        const result = await joinGroupByInvite(inviteCode);
        
        if (result.success && result.group_id) {
          setStatus('success');
          
          // Redirect to catalog (start page) after 2 seconds
          setTimeout(() => {
            navigate('/');
          }, 2000);
        } else {
          setStatus('error');
          setErrorMessage(result.error || 'Ungültiger Einladungscode.');
        }
      } catch (error) {
        console.error('Error processing group invite:', error);
        setStatus('error');
        setErrorMessage('Fehler beim Beitreten der Gruppe.');
      }
    };

    processInvite();
  }, [location.hash, navigate]);

  return (
    <div className="qr-scan-handler">
      <div className="scan-status-card">
        {status === 'processing' && (
          <>
            <div className="spinner"></div>
            <h2>Einladung wird verarbeitet...</h2>
            <p>Bitte warten Sie einen Moment.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="success-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h2>Erfolgreich beigetreten!</h2>
            <p>Du bist jetzt Mitglied der Gruppe.</p>
            <p className="redirect-info">Du wirst zur Startseite weitergeleitet...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="error-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h2>Fehler beim Beitreten</h2>
            <p className="error-message">{errorMessage}</p>
            {!isOnline && (
              <div className="offline-notice">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                  <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
                  <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
                  <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
                  <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
                  <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
                  <line x1="12" y1="20" x2="12.01" y2="20"></line>
                </svg>
                <span>Offline - Bitte stelle eine Internetverbindung her</span>
              </div>
            )}
            <button 
              className="back-button"
              onClick={() => navigate('/')}
            >
              Zur Startseite
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GroupInviteHandler;
